/**
 * useParseClasses Hook
 * 
 * Fetches the database schemas directly from Parse Server REST API.
 * Returns a list of all classes with their field definitions and row counts.
 * 
 * Requires: parseServerURL, appId, and masterKey
 */

import { useState, useEffect, useCallback } from 'react';
import { ParseSchema, ParseSchemasResponse, ParseClass } from '../types/AppPanel';

interface UseParseClassesParams {
  /** Parse Server URL (e.g., https://parseapi.back4app.com/parse) */
  parseServerURL?: string;
  /** Application ID */
  appId?: string;
  /** Master Key (required for /schemas endpoint) */
  masterKey?: string;
}

interface UseParseClassesResult {
  /** Raw schemas from Parse Server */
  schemas: ParseSchema[];
  /** Processed classes with row counts (simplified for UI) */
  classes: ParseClass[];
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch the schemas and counts */
  refetch: () => void;
}

/**
 * Fetch the count of documents in a class
 */
async function fetchClassCount(
  baseUrl: string,
  appId: string,
  masterKey: string,
  className: string
): Promise<number> {
  try {
    const response = await fetch(
      `${baseUrl}/classes/${className}?limit=0&count=1`,
      {
        method: 'GET',
        headers: {
          'X-Parse-Application-Id': appId,
          'X-Parse-Master-Key': masterKey,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      return 0;
    }

    const data = await response.json();
    return data.count ?? 0;
  } catch {
    return 0;
  }
}

/**
 * Hook to fetch Parse Server schemas with row counts
 * 
 * @param params - Parse Server connection parameters
 * @returns Schema data and loading state
 */
export function useParseClasses({
  parseServerURL,
  appId,
  masterKey,
}: UseParseClassesParams): UseParseClassesResult {
  const [schemas, setSchemas] = useState<ParseSchema[]>([]);
  const [classes, setClasses] = useState<ParseClass[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSchemasAndCounts = useCallback(async () => {
    if (!parseServerURL || !appId || !masterKey) {
      setSchemas([]);
      setClasses([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Normalize URL - ensure it ends properly
      const baseUrl = parseServerURL.replace(/\/+$/, '');
      
      // Step 1: Fetch schemas
      const response = await fetch(`${baseUrl}/schemas`, {
        method: 'GET',
        headers: {
          'X-Parse-Application-Id': appId,
          'X-Parse-Master-Key': masterKey,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Invalid application ID or master key');
        }
        if (response.status === 403) {
          throw new Error('Access forbidden - check master key');
        }
        throw new Error(`Failed to fetch schemas: ${response.status}`);
      }

      const data: ParseSchemasResponse = await response.json();
      const fetchedSchemas = data.results || [];
      setSchemas(fetchedSchemas);

      // Step 2: Filter classes we want to show
      const filteredSchemas = fetchedSchemas.filter(schema => {
        const className = schema.className;
        // Skip internal system classes except _User, _Role, _Session
        if (className.startsWith('_')) {
          return className === '_User' || className === '_Role' || className === '_Session';
        }
        return true;
      });

      // Step 3: Fetch counts for all classes in parallel
      const classesWithCounts = await Promise.all(
        filteredSchemas.map(async (schema) => {
          const count = await fetchClassCount(baseUrl, appId, masterKey, schema.className);
          return {
            name: schema.className,
            rowCount: count,
            fields: Object.keys(schema.fields).filter(
              // Filter out default Parse fields for cleaner display
              f => !['objectId', 'createdAt', 'updatedAt', 'ACL'].includes(f)
            ),
          };
        })
      );

      // Step 4: Sort classes - user classes first, then system classes
      const sortedClasses = classesWithCounts.sort((a, b) => {
        const aIsSystem = a.name.startsWith('_');
        const bIsSystem = b.name.startsWith('_');
        if (aIsSystem && !bIsSystem) return 1;
        if (!aIsSystem && bIsSystem) return -1;
        return a.name.localeCompare(b.name);
      });

      setClasses(sortedClasses);
    } catch (err) {
      console.error('Error fetching parse schemas:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setSchemas([]);
      setClasses([]);
    } finally {
      setIsLoading(false);
    }
  }, [parseServerURL, appId, masterKey]);

  // Fetch on mount and when params change
  useEffect(() => {
    fetchSchemasAndCounts();
  }, [fetchSchemasAndCounts]);

  return {
    schemas,
    classes,
    isLoading,
    error,
    refetch: fetchSchemasAndCounts,
  };
}

export default useParseClasses;
